"use client";

import { useMemo, useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  HINT,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  EVERY_DAY,
  useLocalToday,
  WeekdayToggles,
  weekdaySet,
} from "@/components/tools/study/controls";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import { downloadText } from "@/lib/tool-kit/download";
import {
  dayFormatter,
  isoFromDay,
  randomToken,
} from "@/lib/tool-kit/study/dates";
import {
  buildIcsCalendar,
  ICS_MIME,
  icsUidFactory,
} from "@/lib/tool-kit/study/ics";
import {
  buildReadingPlan,
  READING_ERRORS,
  type ReadingDay,
  readingMinutes,
} from "@/lib/tool-kit/study/reading-plan";

const MAX_DAYS = 730;
const DEFAULT_SPAN_DAYS = 13;

type PagesMode = "total" | "range";
type PlanMode = "deadline" | "pace";

/** English strings. A translated content file overrides these through `ui`. */
export const READING_PLANNER_STRINGS = {
  titleLabel: "What you're reading (optional)",
  titlePlaceholder: "e.g. Middlemarch",
  pagesModeLabel: "Pages to read",
  pagesModes: { total: "Whole book", range: "Page range" },
  totalPagesLabel: "Number of pages",
  fromPageLabel: "First page",
  toPageLabel: "Last page",
  planModeLabel: "Plan by",
  planModes: { deadline: "Finish date", pace: "Pages per day" },
  startLabel: "Start reading on",
  finishLabel: "Finish by",
  paceLabel: "Pages per reading day",
  readingDaysLabel: "Reading days",
  weekdaysShort: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
  speedLabel: "Reading speed (words per minute)",
  wordsPerPageLabel: "Words per page",
  speedHint:
    "Defaults: 250 words a minute and 300 words a page. Dense textbooks have more words per page and are read more slowly.",
  results: "Your Reading Plan",
  pagesPerDay: "Pages per reading day",
  readingDays: "Reading days",
  minutesPerDay: "Minutes per reading day",
  lastDay: "Last reading day",
  pagesSpan: "{min}–{max}",
  minutesValue: "≈ {minutes} min",
  colDay: "Day",
  colDate: "Date",
  colPages: "Pages",
  colCount: "Count",
  colTime: "Time",
  pageRange: "pp. {from}–{to}",
  singlePage: "p. {page}",
  downloadIcs: "Add to calendar (.ics)",
  copyPlan: "Copy plan",
  copied: "Copied!",
  icsFileName: "reading-plan.ics",
  icsCalendarName: "Reading plan",
  icsSummary: "Read {pages}",
  icsSummaryTitled: "{title}: read {pages}",
  icsDescription: "{count} pages, about {minutes} minutes",
  textLine: "{date}: {pages} ({minutes})",
  textLineNoTime: "{date}: {pages}",
  enterPages:
    "Enter the number of pages, or the first and last page, to plan your reading.",
  rangeBackwards: "The last page has to come after the first page.",
  enterDates: "Choose a start date and a finish date.",
  enterPace: "Enter how many pages you'll read on each reading day.",
  finishBeforeStart: "The finish date is before the start date.",
  noReadingDays:
    "There are no reading days in that window. Tick more days or move the finish date.",
  tooLong:
    "That plan runs past {max} days. Read a few more pages a day or pick an earlier finish date.",
} as const;

export default function ReadingPlannerWidget() {
  const strings = useToolUi(READING_PLANNER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [title, setTitle] = useState("");
  const [pagesMode, setPagesMode] = useState<PagesMode>("total");
  const [total, setTotal] = useState("320");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [planMode, setPlanMode] = useState<PlanMode>("deadline");
  const [start, setStart] = useState("");
  const [finish, setFinish] = useState("");
  const [pace, setPace] = useState("25");
  const [readingDays, setReadingDays] = useState<boolean[]>([...EVERY_DAY]);
  const [wpm, setWpm] = useState("250");
  const [wordsPerPage, setWordsPerPage] = useState("300");

  useLocalToday((today) => {
    setStart(isoFromDay(today));
    setFinish(isoFromDay(today + DEFAULT_SPAN_DAYS));
  });

  const weekdays = useMemo(() => weekdaySet(readingDays), [readingDays]);
  const outcome = useMemo(
    () =>
      buildReadingPlan(
        { pagesMode, total, from, to, planMode, start, finish, pace, weekdays },
        MAX_DAYS
      ),
    [pagesMode, total, from, to, planMode, start, finish, pace, weekdays]
  );
  const plan = outcome.plan;
  const problem = outcome.problem;
  const message =
    problem === "tooLong"
      ? t(strings.tooLong, { max: MAX_DAYS })
      : problem && strings[problem];
  const isError = problem !== null && READING_ERRORS.has(problem);
  const speed = parseNumberInput(wpm) ?? 0;
  const perPage = parseNumberInput(wordsPerPage) ?? 0;
  const minutes = (pages: number) =>
    t(strings.minutesValue, {
      minutes: fmt(
        Math.max(1, Math.round(readingMinutes(pages, perPage, speed)))
      ),
    });
  const showMinutes = speed > 0 && perPage > 0;
  const formatDay = useMemo(
    () =>
      dayFormatter(locale, {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
      }),
    [locale]
  );
  const pagesLabel = (day: ReadingDay) =>
    day.from === day.to
      ? t(strings.singlePage, { page: fmt(day.from) })
      : t(strings.pageRange, { from: fmt(day.from), to: fmt(day.to) });

  const counts = plan.map((day) => day.pages);
  const most = Math.max(0, ...counts);
  const fewest = Math.min(most, ...counts);
  const pagesStat =
    fewest === most
      ? fmt(most)
      : t(strings.pagesSpan, { min: fmt(fewest), max: fmt(most) });

  const planText = plan
    .map((day) =>
      t(showMinutes ? strings.textLine : strings.textLineNoTime, {
        date: formatDay(day.day),
        pages: pagesLabel(day),
        minutes: showMinutes ? minutes(day.pages) : "",
      })
    )
    .join("\n");

  function handleIcs() {
    const uid = icsUidFactory(randomToken());
    const name = title.trim();
    downloadText(
      strings.icsFileName,
      buildIcsCalendar({
        prodId: "-//CitationEasy//Reading Planner//EN",
        name: name || strings.icsCalendarName,
        stamp: new Date(),
        events: plan.map((day, index) => ({
          uid: uid(index),
          start: day.day,
          summary: t(name ? strings.icsSummaryTitled : strings.icsSummary, {
            title: name,
            pages: pagesLabel(day),
          }),
          description: showMinutes
            ? t(strings.icsDescription, {
                count: fmt(day.pages),
                minutes: fmt(
                  Math.max(
                    1,
                    Math.round(readingMinutes(day.pages, perPage, speed))
                  )
                ),
              })
            : undefined,
        })),
      }),
      ICS_MIME
    );
  }

  return (
    <>
      <section className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <TextField
            id="rp-title"
            label={strings.titleLabel}
            onChange={setTitle}
            placeholder={strings.titlePlaceholder}
            value={title}
          />
        </div>
        <div className="sm:col-span-2">
          <PillGroup
            label={strings.pagesModeLabel}
            name="rp-pages-mode"
            onChange={setPagesMode}
            options={[
              { value: "total", label: strings.pagesModes.total },
              { value: "range", label: strings.pagesModes.range },
            ]}
            value={pagesMode}
          />
        </div>
        {pagesMode === "total" ? (
          <TextField
            id="rp-total"
            label={strings.totalPagesLabel}
            numeric
            onChange={setTotal}
            value={total}
          />
        ) : (
          <>
            <TextField
              id="rp-from"
              label={strings.fromPageLabel}
              numeric
              onChange={setFrom}
              value={from}
            />
            <TextField
              id="rp-to"
              label={strings.toPageLabel}
              numeric
              onChange={setTo}
              value={to}
            />
          </>
        )}
        <div className="sm:col-span-2">
          <PillGroup
            label={strings.planModeLabel}
            name="rp-plan-mode"
            onChange={setPlanMode}
            options={[
              { value: "deadline", label: strings.planModes.deadline },
              { value: "pace", label: strings.planModes.pace },
            ]}
            value={planMode}
          />
        </div>
        <TextField
          id="rp-start"
          label={strings.startLabel}
          onChange={setStart}
          type="date"
          value={start}
        />
        {planMode === "deadline" ? (
          <TextField
            id="rp-finish"
            label={strings.finishLabel}
            onChange={setFinish}
            type="date"
            value={finish}
          />
        ) : (
          <TextField
            id="rp-pace"
            label={strings.paceLabel}
            numeric
            onChange={setPace}
            value={pace}
          />
        )}
        <div className="sm:col-span-2">
          <WeekdayToggles
            labels={strings.weekdaysShort}
            legend={strings.readingDaysLabel}
            name="rp-day"
            onChange={setReadingDays}
            value={readingDays}
          />
        </div>
        <TextField
          id="rp-wpm"
          label={strings.speedLabel}
          numeric
          onChange={setWpm}
          value={wpm}
        />
        <TextField
          id="rp-wpp"
          label={strings.wordsPerPageLabel}
          numeric
          onChange={setWordsPerPage}
          value={wordsPerPage}
        />
        <p className={`${HINT} sm:col-span-2`}>{strings.speedHint}</p>
      </section>

      <section aria-live="polite" className="space-y-5">
        {message && isError ? <ErrorNotice>{message}</ErrorNotice> : null}
        {message && !isError ? <Notice>{message}</Notice> : null}
        {plan.length > 0 ? (
          <>
            <h2 className={WIDGET_H2}>{strings.results}</h2>
            <StatGrid>
              <StatCard
                emphasis
                label={strings.pagesPerDay}
                value={pagesStat}
              />
              <StatCard label={strings.readingDays} value={fmt(plan.length)} />
              {showMinutes ? (
                <StatCard label={strings.minutesPerDay} value={minutes(most)} />
              ) : null}
              <StatCard
                label={strings.lastDay}
                value={formatDay(plan.at(-1)?.day ?? 0)}
              />
            </StatGrid>
            <div className="max-h-[32rem] overflow-auto rounded-lg border border-border bg-card">
              <table className={TABLE}>
                <thead className="sticky top-0 bg-card">
                  <tr>
                    {[
                      strings.colDay,
                      strings.colDate,
                      strings.colPages,
                      strings.colCount,
                      ...(showMinutes ? [strings.colTime] : []),
                    ].map((label) => (
                      <th
                        className={`${TH} whitespace-nowrap`}
                        key={label}
                        scope="col"
                      >
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {plan.map((day, index) => (
                    <tr key={day.day}>
                      <td className={TD}>{fmt(index + 1)}</td>
                      <td className={`${TD} whitespace-nowrap`}>
                        {formatDay(day.day)}
                      </td>
                      <td className={`${TD} whitespace-nowrap`}>
                        {pagesLabel(day)}
                      </td>
                      <td className={TD}>{fmt(day.pages)}</td>
                      {showMinutes ? (
                        <td className={`${TD} whitespace-nowrap`}>
                          {minutes(day.pages)}
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                className={BUTTON_PRIMARY}
                onClick={handleIcs}
                type="button"
              >
                {strings.downloadIcs}
              </button>
              <CopyButton
                copiedLabel={strings.copied}
                label={strings.copyPlan}
                text={planText}
              />
            </div>
          </>
        ) : null}
      </section>
    </>
  );
}
